import uuid from 'uuid/v4';

import { type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';

import type {
	AIDefinitionsPlugin,
	AIDefinitionsPluginConfig,
	ReadingAidsPopupPropsInState,
} from '../../types';

import { type AIDefinitionsAction, AIDefinitionsActionType } from './actions';
import { createCommand, getPluginState } from './plugin-factory';
import { type OpenReadingAidsPopupSource } from './types';
import { getOpenReadingAidsPopupAnalyticsPayload } from './util/analytics';
import { createKeywordDecorations, findKeywordMatches } from './util/keyword';

export const updateSessionId = ({ sessionId }: { sessionId: string }) => {
	return createCommand<AIDefinitionsAction>(() => {
		return {
			type: AIDefinitionsActionType.UPDATE_SESSION_ID,
			sessionId,
		};
	});
};

export const setCurrentKeywordId = ({ keywordId }: { keywordId: string }) => {
	return createCommand<AIDefinitionsAction>(() => {
		return {
			type: AIDefinitionsActionType.SET_CURRENT_KEYWORD_ID,
			keywordId,
		};
	});
};

export const resetCurrentKeywordId = () => {
	return createCommand<AIDefinitionsAction>(() => {
		return {
			type: AIDefinitionsActionType.RESET_CURRENT_KEYWORD_ID,
		};
	});
};

export const openReadingAidsPopup = ({
	readingAidsPopupProps,
	api,
	source,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	source: OpenReadingAidsPopupSource;
	readingAidsPopupProps: ReadingAidsPopupPropsInState;
}) => {
	const readingAidsSessionId = uuid();

	return createCommand<AIDefinitionsAction>(
		() => {
			return {
				type: AIDefinitionsActionType.OPEN_READING_AIDS_POPUP,
				readingAidsPopupProps: { ...readingAidsPopupProps, sessionId: readingAidsSessionId },
			};
		},
		(tr) => {
			if (api) {
				const payload = getOpenReadingAidsPopupAnalyticsPayload({ source, readingAidsSessionId });
				api.analytics?.actions.attachAnalyticsEvent(payload)(tr);
			}
			return tr;
		},
	);
};

export const closeReadingAidsPopup = () => {
	return createCommand<AIDefinitionsAction>(() => {
		return {
			type: AIDefinitionsActionType.CLOSE_READING_AIDS_POPUP,
		};
	});
};

export const updateKeywordDecorations = ({
	keywordDecorations,
}: {
	keywordDecorations: DecorationSet;
}) => {
	return createCommand<AIDefinitionsAction>((state: EditorState) => {
		return {
			type: AIDefinitionsActionType.UPDATE_KEYWORD_DECORATIONS,
			keywordDecorations,
		};
	});
};

export const removeAllKeywordDecorations = () => {
	return updateKeywordDecorations({ keywordDecorations: DecorationSet.empty });
};

export const findAndHighlightKeywords = async ({
	sessionId,
	editorView,
	config,
}: {
	sessionId: string;
	editorView: EditorView;
	config: AIDefinitionsPluginConfig;
}) => {
	try {
		const res = await config.queryHighlightAcronymsFromText?.({
			format: 'ADF',
			text: JSON.stringify(editorView.state.doc.toJSON()),
		});

		// check if the sessionId has changed since the request was made
		// if so, don't process the response
		const aiDefinitionsPluginState = getPluginState(editorView.state);
		if (sessionId !== aiDefinitionsPluginState.sessionId) {
			return;
		}

		if (res && res.acronymRegex) {
			let decorations = DecorationSet.empty;
			const keywordMatches = findKeywordMatches({
				content: editorView.state.doc,
				regex: res.acronymRegex,
			});
			for (const keywordMatch of keywordMatches) {
				const keywordDecorations = createKeywordDecorations({ keywordMatch });
				decorations = decorations.add(editorView.state.doc, keywordDecorations);
			}
			updateKeywordDecorations({ keywordDecorations: decorations })(
				editorView.state,
				editorView.dispatch,
			);
		} else {
			// TODO log warning?
		}
	} catch (err) {
		// check if the sessionId has changed since the request was made
		// if so, don't process the err
		const aiDefinitionsPluginState = getPluginState(editorView.state);
		if (sessionId !== aiDefinitionsPluginState.sessionId) {
			return;
		}

		// TODO log error?
	}
};

export const onLivePageModeChange = ({
	editorView,
	config,
	livePageMode,
}: {
	editorView: EditorView;
	config: AIDefinitionsPluginConfig;
	livePageMode: ViewMode | undefined;
}) => {
	// we do want to closeReadingAidsPopup if there is one even if autoHighlightEnabled is turned off
	closeReadingAidsPopup()(editorView.state, editorView.dispatch);

	if (!config.autoHighlightEnabled) {
		return;
	}

	// remove all keyword decorations
	removeAllKeywordDecorations()(editorView.state, editorView.dispatch);

	// update sessionId
	const sessionId = uuid();
	updateSessionId({ sessionId })(editorView.state, editorView.dispatch);

	// find and highlight keywords if it's live page view mode
	if (livePageMode === 'view') {
		findAndHighlightKeywords({ sessionId, editorView, config });
	}
};
